import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Sparkles, FileText, Zap, Edit3, Calculator, ArrowLeft, ChevronRight, X } from 'lucide-react';
import { useProjects } from '../contexts/ProjectsContext';
import { BlueprintProvider } from '../contexts/BlueprintContext';
import { AnnotationSaveProvider } from '../contexts/AnnotationSaveContext';
import BlueprintViewer from '../components/BlueprintViewer';
import NewAgentChat, { Citation } from '../components/NewAgentChat';
import { TabbedSidebar } from '../components/TabbedSidebar';
import { FilesPanelSimple } from '../components/FilesPanelSimple';
import { ProjectKeyPanelTree } from '../components/ProjectKeyPanelTree';
import { supabase } from '../lib/supabase';
import { updateEstimateQuantity } from '../services/autoEstimation';
import { fetchComponentTotalsV2 } from '../lib/annotationV2Persistence';
// Removed: key extraction agents (Claude-dependent)
// import { extractKeyFromPage } from '../services/keyExtractionAgent';
// import { extractKeyTwoPass, ExtractedComponent, findLegendPageInPDF, saveLegendPage } from '../services/keyExtractionTwoPass';
import '../styles/EstimationDetail.css';
import { getUniqueComponentName, normalizeComponentColors } from '../utils/componentNameUtils';
import { LEGACY_STANDARD_RECEPTACLE_NAMES } from '../utils/receptacleUtils';
import { pdfjs } from 'react-pdf';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));

const normalizeCitationText = (text?: string) => {
  if (!text) return '';
  return text
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
};

interface FuzzyMatchResult {
  start: number;
  length: number;
  score: number;
}

const levenshteinDistance = (a: string, b: string): number => {
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;

  const prev = new Array(n + 1);
  const curr = new Array(n + 1);

  for (let j = 0; j <= n; j++) {
    prev[j] = j;
  }

  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) {
        curr[j] = prev[j - 1];
      } else {
        curr[j] = Math.min(
          prev[j] + 1,
          curr[j - 1] + 1,
          prev[j - 1] + 1
        );
      }
    }
    for (let j = 0; j <= n; j++) {
      prev[j] = curr[j];
    }
  }

  return prev[n];
};

const findApproximateTextMatch = (text: string, pattern: string): FuzzyMatchResult | null => {
  const patternLength = pattern.length;
  if (!patternLength || !text.length) {
    return null;
  }

  const anchorTokens = pattern.split(' ').filter(Boolean).slice(0, 3);
  const candidatePositions = new Set<number>();

  anchorTokens.forEach((token) => {
    if (token.length < 3) return;
    let idx = text.indexOf(token);
    let iterations = 0;
    const maxIterations = 5000;

    while (idx !== -1 && iterations < maxIterations) {
      candidatePositions.add(Math.max(0, idx - Math.floor(patternLength * 0.2)));
      idx = text.indexOf(token, idx + Math.max(1, token.length));
      iterations++;
    }
  });

  if (candidatePositions.size === 0) {
    const step = Math.max(1, Math.floor(patternLength / 2));
    for (let i = 0; i <= text.length - Math.min(patternLength, text.length); i += step) {
      candidatePositions.add(i);
      if (candidatePositions.size > 2000) break;
    }
  }

  const sortedCandidates = Array.from(candidatePositions).sort((a, b) => a - b);
  const maxWindowOffset = Math.min(60, Math.floor(patternLength * 0.3));
  const windowOffsets: number[] = [0];
  if (maxWindowOffset > 0) {
    windowOffsets.push(-maxWindowOffset, maxWindowOffset);
  }

  let bestMatch: FuzzyMatchResult | null = null;

  for (const start of sortedCandidates) {
    for (const offset of windowOffsets) {
      const windowLength = Math.min(
        text.length - start,
        Math.max(20, patternLength + offset)
      );

      if (windowLength <= 0) continue;

      const windowText = text.slice(start, start + windowLength);
      if (!windowText) continue;

      const distance = levenshteinDistance(windowText, pattern);
      const score = distance / patternLength;

      if (!bestMatch || score < bestMatch.score) {
        bestMatch = { start, length: windowText.length, score };
        if (bestMatch.score <= 0.08) {
          return bestMatch;
        }
      }
    }
  }

  if (bestMatch && bestMatch.score <= 0.25) {
    return bestMatch;
  }

  return null;
};

async function findTextBoundingBoxInPage(page: any, quote: string) {
  const normalizedQuote = normalizeCitationText(quote);
  if (!normalizedQuote) {
    return null;
  }

  try {
    const textContent = await page.getTextContent();
    const viewport = page.getViewport({ scale: 1 });

    let normalizedFull = '';
    const segments: Array<{ start: number; end: number; item: any }> = [];

    textContent.items.forEach((item: any) => {
      const sanitized = normalizeCitationText(item.str);
      if (!sanitized) return;

      const needsPrefix = normalizedFull.length > 0;
      const prefixLength = needsPrefix ? 1 : 0;
      const start = normalizedFull.length + prefixLength;

      normalizedFull += (needsPrefix ? ' ' : '') + sanitized;
      const end = normalizedFull.length;

      segments.push({ start, end, item });
    });

    if (!normalizedFull) {
      return null;
    }

    let matchStart = normalizedFull.indexOf(normalizedQuote);
    let matchLength = normalizedQuote.length;

    if (matchStart === -1) {
      console.log('   🤔 Exact text match not found, attempting fuzzy search...');
      const fuzzyMatch = findApproximateTextMatch(normalizedFull, normalizedQuote);
      if (!fuzzyMatch) {
        console.warn('   ⚠️ Fuzzy search could not locate citation on this page');
        return null;
      }
      console.log('   ✨ Fuzzy match located at position', fuzzyMatch.start, 'with score', fuzzyMatch.score.toFixed(3));
      matchStart = fuzzyMatch.start;
      matchLength = fuzzyMatch.length;
    } else {
      console.log('   ✅ Exact text match found on page');
    }

    console.log('✅ Using match range', matchStart, '-', matchStart + matchLength, 'within normalized text');

    const matchEnd = matchStart + matchLength;
    const matchedItems = segments
      .filter((segment) => segment.end > matchStart && segment.start < matchEnd)
      .map((segment) => segment.item);

    if (matchedItems.length === 0) {
      return null;
    }

    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;

    matchedItems.forEach((item: any) => {
      const x = item.transform?.[4] ?? 0;
      const y = item.transform?.[5] ?? 0;
      const fontSize = Math.hypot(item.transform?.[0] ?? 0, item.transform?.[1] ?? 0) || Math.hypot(item.transform?.[2] ?? 0, item.transform?.[3] ?? 0) || 0;
      const textHeight = item.height ?? fontSize ?? 12;
      const estimatedWidth = item.width ?? (item.str?.length ? item.str.length * ((fontSize || 10) * 0.5) : fontSize || 10);

      const topY = y - textHeight;
      const bottomY = y;
      const rightX = x + estimatedWidth;

      minX = Math.min(minX, x);
      maxX = Math.max(maxX, rightX);
      minY = Math.min(minY, topY);
      maxY = Math.max(maxY, bottomY);
    });

    if (!isFinite(minX) || !isFinite(minY) || !isFinite(maxX) || !isFinite(maxY)) {
      return null;
    }

    const normalizedWidth = (maxX - minX) / viewport.width;
    const normalizedHeight = (maxY - minY) / viewport.height;

    const expandHeight = normalizedHeight * 0.2 || 0.01;

    const boundingBox = {
      top: clamp01((viewport.height - maxY) / viewport.height - expandHeight),
      left: clamp01(minX / viewport.width),
      width: clamp01(Math.max(normalizedWidth, 0.01)),
      height: clamp01(Math.max(normalizedHeight + expandHeight * 2, 0.02))
    };

    return boundingBox;
  } catch (error) {
    console.warn('Failed to find text bounding box in page:', error);
    return null;
  }
}

function EstimationDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { projects } = useProjects();
  const pdfAnnotatorRef = useRef<any>(null);
  const pdfDocumentCacheRef = useRef<Map<string, any>>(new Map());
  const [localEstimation, setLocalEstimation] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentFile, setCurrentFile] = useState<any>(null);
  const [currentBlueprintFile, setCurrentBlueprintFile] = useState<any>(null);
  const [drawingFiles, setDrawingFiles] = useState<any[]>([]);
  const [specsFiles, setSpecsFiles] = useState<any[]>([]);  // Array of all specs files
  const [currentSpecsFile, setCurrentSpecsFile] = useState<any>(null);  // Currently viewed specs file
  const [necaManualFile, setNecaManualFile] = useState<any>(null);
  const [necaPageNumber, setNecaPageNumber] = useState<number>(1);
  const [electricalKey, setElectricalKeyRaw] = useState<any>(null);
  const [keyLoadedFromDB, setKeyLoadedFromDB] = useState(false);

  // Wrapper that normalizes component colors (Power=green, Lighting=gold) for backwards compat
  const setElectricalKey = useCallback((key: any) => {
    if (key && key.components) {
      setElectricalKeyRaw({ ...key, components: normalizeComponentColors(key.components) });
    } else {
      setElectricalKeyRaw(key);
    }
  }, []);
  const agentChatRef = useRef<any>(null);
  const previousAnnotationCountsRef = useRef<Map<string, number>>(new Map()); // Track previous counts
  const [isExtractingKey, setIsExtractingKey] = useState(false);
  const [extractionProgress, setExtractionProgress] = useState('');
  const standardEntriesAddedRef = useRef(false);

  const generateStandardKeyEntries = useCallback(() => {
    const POWER_COLOR = 'rgba(34, 197, 94, 0.6)';

    const S = 64;
    const CX = 32, CY = 32, R = 18;
    const SP = 7; // half line spacing (parallel lines 14px apart)
    const LW = 2; // base line width

    const makeCanvas = () => {
      const c = document.createElement('canvas');
      c.width = S; c.height = S;
      const ctx = c.getContext('2d')!;
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, S, S);
      ctx.strokeStyle = '#000';
      ctx.fillStyle = '#000';
      ctx.lineWidth = LW;
      return { c, ctx };
    };

    type TypeMod = 'duplex' | 'gfci' | 'quad' | 'dedicated' | 'halfswitch';

    const drawSpecialCircuitBackground = (ctx: CanvasRenderingContext2D, typeMod: TypeMod) => {
      if (typeMod !== 'dedicated' && typeMod !== 'halfswitch') return;
      ctx.save();
      ctx.beginPath();
      if (typeMod === 'halfswitch') {
        // Legacy special-circuit look: right half of circle is filled.
        ctx.arc(CX, CY, R, -Math.PI / 2, Math.PI / 2, false);
      } else {
        // Dedicated is special-circuit mirrored to both sides.
        ctx.arc(CX, CY, R, 0, Math.PI * 2);
      }
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    };

    const finishSpecialCircuitBars = (
      ctx: CanvasRenderingContext2D,
      typeMod: TypeMod,
      topY: number,
      bottomY: number
    ) => {
      if (typeMod !== 'dedicated' && typeMod !== 'halfswitch') return;
      ctx.fillStyle = '#fff';
      ctx.fillRect(CX - SP, CY - R, SP * 2, R * 2);
      ctx.fillStyle = '#000';
      ctx.beginPath(); ctx.moveTo(CX - SP, topY); ctx.lineTo(CX - SP, bottomY); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(CX + SP, topY); ctx.lineTo(CX + SP, bottomY); ctx.stroke();
    };

    // --- WALL MOUNTED: two vertical parallel lines from wall (bottom) through circle ---
    // Lines go from bottom edge of canvas up through the circle to the top of the circle
    const makeWall = (typeMod: TypeMod) => {
      const { c, ctx } = makeCanvas();
      const wallY = S - 4; // wall at bottom

      drawSpecialCircuitBackground(ctx, typeMod);

      // Two vertical parallel lines from circle top to wall
      ctx.lineWidth = LW;
      ctx.beginPath(); ctx.moveTo(CX - SP, CY - R); ctx.lineTo(CX - SP, wallY); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(CX + SP, CY - R); ctx.lineTo(CX + SP, wallY); ctx.stroke();

      // GFCI: filled rectangle between the two lines spanning full circle height
      if (typeMod === 'gfci') {
        ctx.fillRect(CX - SP, CY - R, SP * 2, R * 2);
      }

      finishSpecialCircuitBars(ctx, typeMod, CY - R, wallY);

      // Circle outline (drawn on top)
      ctx.lineWidth = LW;
      ctx.beginPath(); ctx.arc(CX, CY, R, 0, Math.PI * 2); ctx.stroke();

      // Quadruplex: two horizontal cross lines at ±SP extending beyond circle
      if (typeMod === 'quad') {
        const cross = R + 4;
        ctx.beginPath(); ctx.moveTo(CX - cross, CY - SP); ctx.lineTo(CX + cross, CY - SP); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(CX - cross, CY + SP); ctx.lineTo(CX + cross, CY + SP); ctx.stroke();
      }

      return c.toDataURL('image/png');
    };

    // --- CEILING MOUNTED: two vertical parallel lines spanning full circle diameter (top to bottom of circle) ---
    const makeCeiling = (typeMod: TypeMod) => {
      const { c, ctx } = makeCanvas();

      drawSpecialCircuitBackground(ctx, typeMod);

      // Parallel lines touching top and bottom of circle
      ctx.lineWidth = LW;
      ctx.beginPath(); ctx.moveTo(CX - SP, CY - R); ctx.lineTo(CX - SP, CY + R); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(CX + SP, CY - R); ctx.lineTo(CX + SP, CY + R); ctx.stroke();

      if (typeMod === 'gfci') {
        ctx.fillRect(CX - SP, CY - R, SP * 2, R * 2);
      }

      finishSpecialCircuitBars(ctx, typeMod, CY - R, CY + R);

      ctx.lineWidth = LW;
      ctx.beginPath(); ctx.arc(CX, CY, R, 0, Math.PI * 2); ctx.stroke();

      if (typeMod === 'quad') {
        ctx.beginPath(); ctx.moveTo(CX - R, CY - SP); ctx.lineTo(CX + R, CY - SP); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(CX - R, CY + SP); ctx.lineTo(CX + R, CY + SP); ctx.stroke();
      }

      return c.toDataURL('image/png');
    };

    // --- FLOOR MOUNTED: same as ceiling but with a square box around it ---
    const makeFloor = (typeMod: TypeMod) => {
      const { c, ctx } = makeCanvas();
      const boxPad = 8;
      const boxR = R + boxPad;

      // Square box
      ctx.lineWidth = LW;
      ctx.strokeRect(CX - boxR, CY - boxR, boxR * 2, boxR * 2);

      drawSpecialCircuitBackground(ctx, typeMod);

      // Parallel lines touching top and bottom of circle
      ctx.lineWidth = LW;
      ctx.beginPath(); ctx.moveTo(CX - SP, CY - R); ctx.lineTo(CX - SP, CY + R); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(CX + SP, CY - R); ctx.lineTo(CX + SP, CY + R); ctx.stroke();

      if (typeMod === 'gfci') {
        ctx.fillRect(CX - SP, CY - R, SP * 2, R * 2);
      }

      finishSpecialCircuitBars(ctx, typeMod, CY - R, CY + R);

      ctx.lineWidth = LW;
      ctx.beginPath(); ctx.arc(CX, CY, R, 0, Math.PI * 2); ctx.stroke();

      if (typeMod === 'quad') {
        ctx.beginPath(); ctx.moveTo(CX - R, CY - SP); ctx.lineTo(CX + R, CY - SP); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(CX - R, CY + SP); ctx.lineTo(CX + R, CY + SP); ctx.stroke();
      }

      return c.toDataURL('image/png');
    };

    // --- FURNITURE MOUNTED: square outline with parallel lines inside ---
    const makeFurniture = () => {
      const { c, ctx } = makeCanvas();
      const boxSize = 28;
      ctx.lineWidth = LW;
      ctx.strokeRect(CX - boxSize / 2, CY - boxSize / 2, boxSize, boxSize);
      ctx.beginPath(); ctx.moveTo(CX - SP, CY - boxSize / 2); ctx.lineTo(CX - SP, CY + boxSize / 2); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(CX + SP, CY - boxSize / 2); ctx.lineTo(CX + SP, CY + boxSize / 2); ctx.stroke();
      return c.toDataURL('image/png');
    };

    // --- Junction box: circle with "J" ---
    const makeJBox = () => {
      const { c, ctx } = makeCanvas();
      ctx.lineWidth = LW;
      ctx.beginPath(); ctx.arc(CX, CY, R, 0, Math.PI * 2); ctx.stroke();
      ctx.font = 'bold 20px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('J', CX, CY + 1);
      return c.toDataURL('image/png');
    };

    // --- Tele/data: triangle ---
    const makeTeleData = () => {
      const { c, ctx } = makeCanvas();
      ctx.lineWidth = LW;
      ctx.beginPath();
      ctx.moveTo(CX, 10); ctx.lineTo(54, 50); ctx.lineTo(10, 50); ctx.closePath();
      ctx.stroke();
      return c.toDataURL('image/png');
    };

    const makeEntry = (name: string, componentType: string, refImage: string) => ({
      color: POWER_COLOR,
      name,
      symbol: name,
      componentType,
      description: 'Auto-generated standard class',
      subscript: '',
      pages: [] as any[],
      totalCount: 0,
      referenceImage: refImage,
      hasReferenceImage: true,
      isStandardClass: true,
    });

    return [
      // Wall mounted (5 types)
      makeEntry('Wall Mounted Duplex Receptacle', 'Receptacles', makeWall('duplex')),
      makeEntry('Wall Mounted GFCI Receptacle', 'Receptacles', makeWall('gfci')),
      makeEntry('Wall Mounted Dedicated Receptacle', 'Receptacles', makeWall('dedicated')),
      makeEntry('Wall Mounted Halfswitch Specific Circuit Receptacle', 'Receptacles', makeWall('halfswitch')),
      makeEntry('Wall Mounted Quadruplex Receptacle', 'Receptacles', makeWall('quad')),
      // Ceiling mounted (5 types)
      makeEntry('Ceiling Mounted Duplex Receptacle', 'Receptacles', makeCeiling('duplex')),
      makeEntry('Ceiling Mounted GFCI Receptacle', 'Receptacles', makeCeiling('gfci')),
      makeEntry('Ceiling Mounted Dedicated Receptacle', 'Receptacles', makeCeiling('dedicated')),
      makeEntry('Ceiling Mounted Halfswitch Specific Circuit Receptacle', 'Receptacles', makeCeiling('halfswitch')),
      makeEntry('Ceiling Mounted Quadruplex Receptacle', 'Receptacles', makeCeiling('quad')),
      // Floor mounted (5 types)
      makeEntry('Floor Mounted Duplex Receptacle', 'Receptacles', makeFloor('duplex')),
      makeEntry('Floor Mounted GFCI Receptacle', 'Receptacles', makeFloor('gfci')),
      makeEntry('Floor Mounted Dedicated Receptacle', 'Receptacles', makeFloor('dedicated')),
      makeEntry('Floor Mounted Halfswitch Specific Circuit Receptacle', 'Receptacles', makeFloor('halfswitch')),
      makeEntry('Floor Mounted Quadruplex Receptacle', 'Receptacles', makeFloor('quad')),
      // Furniture mounted (5 types)
      makeEntry('Furniture Mounted Duplex Receptacle', 'Receptacles', makeFurniture()),
      makeEntry('Furniture Mounted GFCI Receptacle', 'Receptacles', makeFurniture()),
      makeEntry('Furniture Mounted Dedicated Receptacle', 'Receptacles', makeFurniture()),
      makeEntry('Furniture Mounted Halfswitch Specific Circuit Receptacle', 'Receptacles', makeFurniture()),
      makeEntry('Furniture Mounted Quadruplex Receptacle', 'Receptacles', makeFurniture()),
      // Junction boxes
      makeEntry('Junction Box', 'Distribution Equipment', makeJBox()),
      // Tele/data
      makeEntry('Tele/Data Outlet', 'Telecom', makeTeleData()),
    ];
  }, []);

  // Auto-add standard entries to key on project load (one-time per session)
  useEffect(() => {
    if (!id || !electricalKey || standardEntriesAddedRef.current) return;
    standardEntriesAddedRef.current = true;

    const existingComponents = electricalKey.components || [];
    const legacyNames = new Set(LEGACY_STANDARD_RECEPTACLE_NAMES);
    const filteredExisting = existingComponents.filter((c: any) =>
      !(c.isStandardClass && legacyNames.has(c.name || ''))
    );
    const existingNames = new Set(filteredExisting.map((c: any) => c.name));
    const allStandards = generateStandardKeyEntries();
    const missing = allStandards.filter(s => !existingNames.has(s.name));
    const removedLegacyCount = existingComponents.length - filteredExisting.length;

    if (missing.length === 0 && removedLegacyCount === 0) return;

    const updatedKey = {
      ...electricalKey,
      components: [...filteredExisting, ...missing],
    };

    console.log(`⚡ Standard key migration: +${missing.length} added, -${removedLegacyCount} legacy removed`);
    setElectricalKey(updatedKey);

    supabase
      .from('estimations')
      .update({ project_key: updatedKey })
      .eq('id', id)
      .then(({ error }) => {
        if (error) console.error('Failed to save standard entries:', error);
        else console.log(`✅ Saved ${missing.length} standard entries to Supabase`);
      });
  }, [id, electricalKey, generateStandardKeyEntries, setElectricalKey]);

  // Also ensure standards exist if key is null (first time, no extraction yet)
  useEffect(() => {
    if (!id || !keyLoadedFromDB || electricalKey || standardEntriesAddedRef.current) return;
    standardEntriesAddedRef.current = true;

    const allStandards = generateStandardKeyEntries();
    const newKey = {
      components: allStandards,
      extractedAt: new Date().toISOString(),
      pdfHash: '',
    };

    console.log(`⚡ No key exists — creating with ${allStandards.length} standard entries`);
    setElectricalKey(newKey);

    supabase
      .from('estimations')
      .update({ project_key: newKey })
      .eq('id', id)
      .then(({ error }) => {
        if (error) console.error('Failed to save new standard key:', error);
        else console.log(`✅ Created new key with ${allStandards.length} standard entries`);
      });
  }, [id, keyLoadedFromDB, electricalKey, generateStandardKeyEntries, setElectricalKey]);
  const [isAgentChatCollapsed, setIsAgentChatCollapsed] = useState(false); // Open by default
  const [activeMode, setActiveMode] = useState<'draw' | 'specs' | 'rfi' | 'neca' | 'estimate'>('draw');
  const [annotationCounts, setAnnotationCounts] = useState<Map<string, Map<number, number>>>(new Map());
  const [filteredComponent, setFilteredComponent] = useState<string | null>(null);
  const [countingModeActive, setCountingModeActive] = useState(false);
  const [activePlacementComponent, setActivePlacementComponent] = useState<{ name: string; symbol: string; color: string; symbolType?: string } | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [autoExtractKeyOnSnapshot, setAutoExtractKeyOnSnapshot] = useState(false);
  const [keyExtractionProgress, setKeyExtractionProgress] = useState('');
  const [isFilesPanelCollapsed, setIsFilesPanelCollapsed] = useState(true);
  const [isKeyPanelCollapsed, setIsKeyPanelCollapsed] = useState(true);
  const [isIndexingDrawing, setIsIndexingDrawing] = useState(false);
  const [pageIndex, setPageIndex] = useState<any[] | null>(null);

  const getPdfDocument = useCallback(async (fileUrl: string) => {
    if (!fileUrl) return null;

    if (pdfDocumentCacheRef.current.has(fileUrl)) {
      console.log('🗂️ Using cached PDF for citation search:', fileUrl);
      return pdfDocumentCacheRef.current.get(fileUrl);
    }

    try {
      console.log('📥 Loading PDF for citation search:', fileUrl);
      const loadingTask = pdfjs.getDocument(fileUrl);
      const pdfDocument = await loadingTask.promise;
      pdfDocumentCacheRef.current.set(fileUrl, pdfDocument);
      console.log('✅ PDF loaded for citation search:', fileUrl);
      return pdfDocument;
    } catch (error) {
      console.warn('Failed to load PDF document for citation search:', error);
      return null;
    }
  }, []);

  const locateCitationInSpecs = useCallback(async (citation: Citation) => {
    if (!citation?.quote || specsFiles.length === 0) {
      console.warn('⚠️ Missing citation quote or specs files; skipping text search');
      return null;
    }

    console.log('🔎 Searching for citation text:', citation.quote.slice(0, 80), '...');

    const filesToSearch: any[] = [];

    if (citation.fileId) {
      const hintedFile = specsFiles.find(file => file.id === citation.fileId);
      if (hintedFile) {
        filesToSearch.push(hintedFile);
        console.log('📄 Using Gemini-hinted file only:', hintedFile.file_name);
      } else {
        console.warn('⚠️ Gemini referenced a file we cannot find. Searching all files as fallback.');
        specsFiles.forEach((file) => filesToSearch.push(file));
      }
    } else {
      specsFiles.forEach((file) => filesToSearch.push(file));
    }

    for (const file of filesToSearch) {
      console.log('📘 Searching file for citation:', file.file_name);
      const pdfDocument = await getPdfDocument(file.url);
      if (!pdfDocument) {
          console.warn('⚠️ Could not load PDF for file; skipping:', file.file_name);
        continue;
      }

      const totalPages = pdfDocument.numPages || 0;
      if (totalPages === 0) {
        console.warn('⚠️ PDF has 0 pages; skipping file:', file.file_name);
        continue;
      }

      const pageOrder: number[] = [];
      const hasPageHint = citation.pageNumber && citation.pageNumber >= 1 && citation.pageNumber <= totalPages;
      if (hasPageHint) {
        pageOrder.push(citation.pageNumber!);
        console.log('➡️ Trying hinted page only:', citation.pageNumber);
      } else {
        for (let pageNumber = 1; pageNumber <= totalPages; pageNumber++) {
          pageOrder.push(pageNumber);
        }
      }

      for (const pageNumber of pageOrder) {
        try {
          console.log(`   🔍 Searching page ${pageNumber}/${totalPages} for quote...`);
          const page = await pdfDocument.getPage(pageNumber);
          const boundingBox = await findTextBoundingBoxInPage(page, citation.quote);
          page.cleanup();

          if (boundingBox) {
            console.log('   ✅ Found citation text on page', pageNumber, 'in file', file.file_name);
            return {
              file,
              pageNumber,
              boundingBox
            };
          }
          console.log('   ↪️ Text not on this page; continuing search.');
        } catch (error) {
          console.warn(`Failed to search page ${pageNumber} in ${file.file_name}:`, error);
        }
      }

      console.log('   ❌ Text not found in file:', file.file_name);
      if (hasPageHint) {
        console.log('   ⚠️ Skipping wider search because only hinted page should be searched');
        continue;
      }
    }

    console.warn('🚫 Citation text not found in any specs file');
    return null;
  }, [specsFiles, getPdfDocument]);

  const handleNavigateToSpecsPage = useCallback(async (citation: Citation) => {
    if (!citation) {
      return;
    }

    setActiveMode('specs');

    try {
      let resolvedFile = citation.fileId ? specsFiles.find(file => file.id === citation.fileId) : null;
      let resolvedPage = citation.pageNumber || 1;
      let resolvedBoundingBox = citation.boundingBox;

      if (citation.quote) {
        const located = await locateCitationInSpecs(citation);
        if (located) {
          resolvedFile = located.file;
          resolvedPage = located.pageNumber;
          resolvedBoundingBox = located.boundingBox;
        }
      }

      const targetFile = resolvedFile || specsFiles[0];

      if (!targetFile) {
        console.warn('No specifications files available to navigate to citation');
        return;
      }

      setCurrentSpecsFile(targetFile);

      setTimeout(() => {
        pdfAnnotatorRef.current?.navigateToSpecsPage(resolvedPage, resolvedBoundingBox);
      }, 400);
    } catch (error) {
      console.error('Failed to navigate to specs citation:', error);
    }
  }, [locateCitationInSpecs, specsFiles]);

  // Auto-collapse key panel when switching to specs mode
  useEffect(() => {
    if (activeMode === 'specs') {
      setIsKeyPanelCollapsed(true);
    }
  }, [activeMode]);

  // Switch file based on active mode
  useEffect(() => {
    console.log('🔄 Active mode changed:', activeMode, {
      hasBlueprint: !!currentBlueprintFile,
      hasSpecs: !!currentSpecsFile,
      hasNeca: !!necaManualFile
    });
    
    if (activeMode === 'draw' && currentBlueprintFile) {
      console.log('→ Switching to blueprint');
      setCurrentFile(currentBlueprintFile);
    } else if (activeMode === 'specs' && currentSpecsFile) {
      console.log('→ Switching to specs');
      setCurrentFile(currentSpecsFile);
    } else if (activeMode === 'neca' && necaManualFile) {
      console.log('→ Switching to NECA manual:', necaManualFile);
      console.log('   URL:', necaManualFile.url);
      console.log('   Storage path:', necaManualFile.storage_path);
      setCurrentFile(necaManualFile);
    } else if (activeMode === 'draw' || activeMode === 'specs' || activeMode === 'rfi' || activeMode === 'estimate') {
      // Clear file when no blueprint/specs available for this mode (or mode doesn't need a file)
      console.log('→ No file for this mode, clearing');
      setCurrentFile(null);
    }
  }, [activeMode, currentBlueprintFile, currentSpecsFile, necaManualFile]);

  // Try to find estimation in context first
  const contextEstimation = projects.find(p => p.id === id);
  // Prefer localEstimation if it has cost_info (from seeding), otherwise use context
  const estimation = (localEstimation?.cost_info ? localEstimation : contextEstimation) || localEstimation;

  useEffect(() => {
    // If estimation is found in context, copy it to local state so we can update it after seeding
    if (contextEstimation) {
      setLocalEstimation(contextEstimation);
      setIsLoading(false);
      // Still need to load project_key from database (which may trigger seeding)
      loadProjectKeyFromDB();
      return;
    }

    // Otherwise, load it directly from Supabase
    const loadEstimation = async () => {
      try {
        const { data, error } = await supabase
          .from('estimations')
          .select('*')
          .eq('id', id)
          .single();

        if (error) {
          console.error('Error loading estimation:', error);
          setIsLoading(false);
          return;
        }

        if (data) {
          console.log('🔍 Loaded estimation from DB:', {
            id: data.id,
            name: data.name,
            hasProjectKey: !!data.project_key,
            componentCount: data.project_key?.components?.length || 0
          });
          
          // Format the data to match the expected structure
          const formattedEstimation = {
            ...data,
            blueprintFile: data.blueprint_url ? {
              url: data.blueprint_url,
              name: data.blueprint_filename || 'Blueprint.pdf'
            } : undefined
          };
          setLocalEstimation(formattedEstimation);
          // Set electrical key if it exists
          if (data.project_key) {
            console.log('✅ Setting electrical key with', data.project_key.components?.length || 0, 'components');
            setElectricalKey(data.project_key);
          }
        }
      } catch (err) {
        console.error('Failed to load estimation:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadEstimation();
  }, [id, contextEstimation]);

  // Separate function to load just the project_key
  const loadProjectKeyFromDB = async () => {
    if (!id) return;
    
    try {
      const { data, error } = await supabase
        .from('estimations')
        .select('project_key')
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error loading project_key:', error);
        return;
      }

      if (data?.project_key) {
        console.log('✅ Loaded project_key with', data.project_key.components?.length || 0, 'components');
        setElectricalKey(data.project_key);
      } else {
        console.log('⚠️ No project_key found in database — will seed with standard entries');
      }
      setKeyLoadedFromDB(true);
    } catch (err) {
      console.error('Failed to load project_key:', err);
      setKeyLoadedFromDB(true); // Still mark as loaded so seeding can proceed
    }
  };

  // Load project_key when estimation ID is available
  useEffect(() => {
    if (estimation?.id) {
      setKeyLoadedFromDB(false);
      loadProjectKeyFromDB();
    }
  }, [estimation?.id]);

  const handleDrawingFileChange = (fileId: string) => {
    const selected = drawingFiles.find((file) => file.id === fileId);
    if (selected) {
      setCurrentBlueprintFile(selected);
      if (activeMode === 'draw') {
        setCurrentFile(selected);
      }
    }
  };

  // Load NECA Manual (from Supabase Storage)
  useEffect(() => {
    const loadNecaManual = async () => {
      try {
        // Get public URL using Supabase storage method (same as specs)
        const storagePath = 'neca/neca-manual-2015-16.pdf';
        const { data: urlData } = supabase.storage
          .from('bucket')
          .getPublicUrl(storagePath);
        
        const necaFile = {
          id: 'neca-manual-default',
          file_name: 'NECA Manual of Labor Units 2015-16',
          url: urlData.publicUrl, // Use Supabase public URL
          storage_path: storagePath,
          file_size: 27329536, // ~26 MB
          file_type: 'application/pdf'
        };
        
        console.log('📖 Setting NECA manual file:', necaFile);
        console.log('   Public URL:', urlData.publicUrl);
        setNecaManualFile(necaFile);
      } catch (error) {
        console.error('❌ Error loading NECA manual:', error);
      }
    };
    
    loadNecaManual();
  }, []);

  // Load blueprint and specs files from Supabase
  useEffect(() => {
    if (!estimation?.id) return;

    const loadFiles = async () => {
      try {
        // Load blueprint files
        const { data: blueprintFiles, error: bpError } = await supabase
          .from('estimation_files')
          .select('*')
          .eq('estimation_id', estimation.id)
          .order('file_order', { ascending: true });

        if (bpError) {
          console.error('Error loading drawing files:', bpError);
        } else if (blueprintFiles && blueprintFiles.length > 0) {
          const drawingsWithUrls = blueprintFiles.map((bp) => {
          const { data: urlData } = supabase.storage
            .from('bucket')
              .getPublicUrl(bp.storage_path);

            return {
              id: bp.id,
              file_name: bp.file_name,
              name: bp.file_name,
              storage_path: bp.storage_path,
              url: urlData.publicUrl,
              file_size: bp.file_size,
              file_type: bp.file_type
          };
          });

          setDrawingFiles(drawingsWithUrls);

          const existingSelection = currentBlueprintFile
            ? drawingsWithUrls.find((file) => file.id === currentBlueprintFile.id)
            : null;
          const nextSelection = existingSelection || drawingsWithUrls[0];

          if (nextSelection) {
            setCurrentBlueprintFile(nextSelection);
            setCurrentFile(nextSelection);
          }
        } else {
          setDrawingFiles([]);
          setCurrentBlueprintFile(null);
        }

        // Load ALL specs files
        const { data: specsFilesData, error: specsError } = await supabase
          .from('estimation_specs')
          .select('*')
          .eq('estimation_id', estimation.id)
          .order('file_order', { ascending: true });

        if (specsError) {
          console.error('Error loading specs files:', specsError);
        } else if (specsFilesData && specsFilesData.length > 0) {
          // Load all specs files with public URLs
          const specsWithUrls = specsFilesData.map(spec => {
            const { data: urlData } = supabase.storage
              .from('bucket')
              .getPublicUrl(spec.storage_path);

            return {
              id: spec.id,
              file_name: spec.file_name,
              storage_path: spec.storage_path,
              url: urlData.publicUrl,
              file_size: spec.file_size,
              file_type: spec.file_type
            };
          });

          setSpecsFiles(specsWithUrls);
          // Set first file as current by default
          setCurrentSpecsFile(specsWithUrls[0]);
          
          console.log(`✅ Loaded ${specsWithUrls.length} specification file(s)`);
        }
      } catch (err) {
        console.error('Failed to load files:', err);
      }
    };

    loadFiles();
  }, [estimation?.id]);

  // Check and trigger page indexing if page_index is null
  useEffect(() => {
    if (!estimation?.id || !drawingFiles.length) return;

    // If page_index already exists on the estimation, use it
    if (estimation.page_index) {
      setPageIndex(estimation.page_index);
      return;
    }

    // page_index is null — trigger indexing
    setIsIndexingDrawing(true);
    console.log('📋 Page index missing — triggering drawing page indexing...');

    const indexDrawing = async () => {
      try {
        const response = await fetch('/api/index-drawing-pages', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            estimationId: estimation.id,
            fileUri: drawingFiles[0].url,
          }),
        });
        const data = await response.json();
        if (data.pageIndex) {
          console.log(`✅ Page index created: ${data.pageIndex.length} pages`);
          setPageIndex(data.pageIndex);
          // Update local estimation so we don't re-trigger
          setLocalEstimation((prev: any) => prev ? { ...prev, page_index: data.pageIndex } : prev);
        }
      } catch (err) {
        console.error('❌ Failed to index drawing pages:', err);
      } finally {
        setIsIndexingDrawing(false);
      }
    };

    indexDrawing();
  }, [estimation?.id, estimation?.page_index, drawingFiles]);

  const handleReindex = async () => {
    if (!estimation?.id || !drawingFiles.length) return;
    setIsIndexingDrawing(true);
    try {
      const response = await fetch('/api/index-drawing-pages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          estimationId: estimation.id,
          fileUri: drawingFiles[0].url,
          force: true,
        }),
      });
      const data = await response.json();
      if (data.pageIndex) {
        setPageIndex(data.pageIndex);
        setLocalEstimation((prev: any) => prev ? { ...prev, page_index: data.pageIndex } : prev);
      }
    } catch (err) {
      console.error('❌ Failed to reindex drawing pages:', err);
    } finally {
      setIsIndexingDrawing(false);
    }
  };

  // Subscribe to electrical key changes
  useEffect(() => {
    if (!estimation?.id) return;

    const channel = supabase
      .channel(`electrical-key-${estimation.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'estimations',
          filter: `id=eq.${estimation.id}`
        },
        (payload) => {
          if (payload.new && (payload.new as any).project_key) {
            console.log('🔄 Electrical key updated, refreshing annotations');
            setElectricalKey((payload.new as any).project_key);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [estimation?.id]);

  // Handler for extracting key - DISABLED (Claude agents removed)
  const handleExtractKeyFromPage = async () => {
    setExtractionProgress('Key extraction feature is currently disabled');
    setTimeout(() => setExtractionProgress(''), 3000);
  };

  if (isLoading) {
    return (
      <div className="estimation-loading">
        <div className="loading-spinner"></div>
        <p>Loading estimation...</p>
      </div>
    );
  }

  if (!estimation) {
    return (
      <div className="estimation-not-found">
        <h2>Estimation not found</h2>
        <button onClick={() => navigate('/')} className="btn btn-primary">
          Back to Dashboard
        </button>
      </div>
    );
  }

  if (isIndexingDrawing) {
    return (
      <div className="estimation-loading">
        <div className="loading-spinner"></div>
        <p>Indexing drawing pages...</p>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '4px' }}>
          Analyzing each page to identify sheet types
        </p>
      </div>
    );
  }
  
  return (
    <AnnotationSaveProvider>
    <BlueprintProvider>
      <div className="estimation-detail-container">        {/* New Layout: PDF Canvas with chat on right, key on bottom, files on left */}
        <div className="estimation-main-layout">
          {/* Left: Files Panel (collapsible) */}
          <div className={`files-sidebar ${isFilesPanelCollapsed ? 'hidden' : ''}`}>
            <FilesPanelSimple
              estimationId={estimation.id || ''}
              onFileSelect={(file) => {
                console.log('Selected file:', file);
                setCurrentFile(file);
              }}
              currentFile={currentFile}
              onCollapse={() => setIsFilesPanelCollapsed(true)}
              projectName={estimation.name}
            />
          </div>

          {/* Center: PDF Canvas with top controls + Right: Chat */}
          <div className="canvas-and-chat-container">
            {/* No top bar - controls moved to PDF annotator */}

            {/* PDF Canvas + Chat Row */}
            <div className="canvas-chat-row">
              {/* PDF Canvas */}
              <div className="canvas-panel">
                <BlueprintViewer
                  ref={pdfAnnotatorRef}
                  file={currentFile ? { 
                    url: currentFile.url || currentFile.storage_path, 
                    name: currentFile.file_name || currentFile.name, 
                    id: currentFile.id,
                    file_type: currentFile.file_type,
                    type: currentFile.type
                  } : null}
                  estimationId={estimation.id}
                  electricalKey={electricalKey}
                  activeMode={activeMode}
                  specsFiles={specsFiles}
                  currentSpecsFile={currentSpecsFile}
                  onSpecsFileChange={(fileId: string) => {
                    const newFile = specsFiles.find(f => f.id === fileId);
                    if (newFile) {
                      setCurrentSpecsFile(newFile);
                    }
                  }}
                  drawingFiles={drawingFiles}
                  currentDrawingFile={currentBlueprintFile}
                  onDrawingChange={handleDrawingFileChange}
                  currentDrawingFileId={currentBlueprintFile?.id}
                  annotationCounts={annotationCounts}
                  autoExtractKeyOnSnapshot={autoExtractKeyOnSnapshot}
                  estimation={estimation}
                  isFilesPanelCollapsed={isFilesPanelCollapsed}
                  onFilesToggle={() => setIsFilesPanelCollapsed(!isFilesPanelCollapsed)}
                  onHomeClick={() => navigate('/')}
                  onModeChange={(mode: any) => setActiveMode(mode)}
                  isAgentChatCollapsed={isAgentChatCollapsed}
                  onChatToggle={() => setIsAgentChatCollapsed(!isAgentChatCollapsed)}
                  onScreenshotCaptured={(base64) => {
                    if (agentChatRef.current?.handleScreenshotCaptured) {
                      agentChatRef.current.handleScreenshotCaptured(base64);
                    }
                  }}
                  onAutoAnnotationTrigger={(config) => {
                    if (agentChatRef.current?.handleAutoAnnotation) {
                      agentChatRef.current.handleAutoAnnotation(config);
                    }
                  }}
                  onKeyExtractionProgress={(progress) => {
                    setKeyExtractionProgress(progress);
                  }}
                  onKeyExtracted={async (extractedKey) => {
                    setAutoExtractKeyOnSnapshot(false);
                    console.log('🔑 Key extracted from PDF, saving to Supabase...', extractedKey);
                    
                    try {
                      const { data, error} = await supabase
                        .from('estimations')
                        .update({ project_key: extractedKey })
                        .eq('id', estimation.id)
                        .select('project_key')
                        .single();
                      
                      if (error) {
                        console.error('❌ Error saving extracted key:', error);
                        setKeyExtractionProgress(`❌ Failed to save: ${error.message}`);
                      } else {
                        console.log('✅ Key saved successfully to Supabase');
                        setElectricalKey(data.project_key);
                        setRefreshTrigger(prev => prev + 1); // Trigger ProjectKeyPanel refresh
                        setKeyExtractionProgress(`Extracted ${extractedKey.components?.length || 0} symbols`);
                        setTimeout(() => setKeyExtractionProgress(''), 2000);
                      }
                    } catch (error) {
                      console.error('❌ Error in key extraction callback:', error);
                      setKeyExtractionProgress('❌ Failed to save key');
                    }
                  }}
                  countingModeActive={countingModeActive}
                  activePlacementComponent={activePlacementComponent}
                  onPlacementComplete={() => setActivePlacementComponent(null)}
                  onStopCounting={() => {
                    setActivePlacementComponent(null);
                    agentChatRef.current?.switchToAssistant?.();
                  }}
                  onAnnotationSaved={async (changedComponentTypes?: string[]) => {
                    console.log('📢 Annotation saved, triggering count refresh and estimate sync', {
                      changedComponentTypes
                    });
                    setRefreshTrigger(prev => prev + 1);
                    
                    if (estimation?.id && electricalKey?.components) {
                      const targetComponentNames = (
                        changedComponentTypes && changedComponentTypes.length > 0
                          ? Array.from(new Set(changedComponentTypes.filter(Boolean)))
                          : electricalKey.components.map((c: any) => c.name)
                      );

                      if (targetComponentNames.length === 0) return;

                      // Quick targeted poll by estimation_id + changed symbol names
                      const currentCounts = await fetchComponentTotalsV2({
                        supabase: supabase as any,
                        estimationId: estimation.id,
                        componentTypes: targetComponentNames,
                      });

                      // Ensure deleted components resolve to zero
                      for (const componentName of targetComponentNames) {
                        if (!currentCounts.has(componentName)) {
                          currentCounts.set(componentName, 0);
                        }
                      }
                      
                      const changedComponents: string[] = [];
                      for (const componentName of targetComponentNames) {
                        const newCount = currentCounts.get(componentName) || 0;
                        const keyComponent = electricalKey.components.find((c: any) =>
                          c.name.toLowerCase() === componentName.toLowerCase()
                        );

                        // If ref isn't primed yet, fall back to key totalCount so we don't miss manual edits
                        const hadPreviousCount = previousAnnotationCountsRef.current.has(componentName);
                        const previousCount = hadPreviousCount
                          ? (previousAnnotationCountsRef.current.get(componentName) || 0)
                          : (typeof keyComponent?.totalCount === 'number' ? keyComponent.totalCount : 0);

                        if (previousCount !== newCount) {
                          changedComponents.push(componentName);
                          console.log(`📊 Count changed for "${componentName}": ${previousCount} → ${newCount}`);
                          
                          const success = await updateEstimateQuantity(
                            estimation.id,
                            componentName,
                            newCount,
                            keyComponent?.estimate_matched_component
                          );
                          
                          if (success) {
                            console.log(`✅ Updated estimate for "${componentName}" to ${newCount}`);
                          } else {
                            console.warn(`⚠️ Could not find estimate row for "${componentName}" (will be created on autocount)`);
                          }
                        }
                        
                        // Always update the ref with new count (for future comparisons)
                        previousAnnotationCountsRef.current.set(componentName, newCount);
                      }
                      
                      if (changedComponents.length === 0) {
                        console.log('ℹ️ No component counts changed, skipping estimate updates');
                      } else {
                        console.log(`✅ Updated ${changedComponents.length} component(s):`, changedComponents.join(', '));

                        // Persist changed totalCount values back to project_key so key stays in sync
                        const updatedComponents = (electricalKey.components || []).map((component: any) => {
                          const matchedName = targetComponentNames.find(
                            (name: string) => name.toLowerCase() === component.name.toLowerCase()
                          );
                          if (!matchedName) return component;
                          const nextCount = currentCounts.get(matchedName);
                          if (typeof nextCount !== 'number') return component;
                          return {
                            ...component,
                            totalCount: nextCount,
                          };
                        });

                        const updatedKey = {
                          ...electricalKey,
                          components: updatedComponents
                        };

                        // Update local state immediately and then persist
                        setElectricalKey(updatedKey as any);
                        const { error: keyUpdateError } = await supabase
                          .from('estimations')
                          .update({ project_key: updatedKey })
                          .eq('id', estimation.id);

                        if (keyUpdateError) {
                          console.error('❌ Failed to persist project key counts:', keyUpdateError);
                        } else {
                          console.log('✅ Project key counts persisted');
                        }
                      }
                    }
                  }}
                  onSwitchToCountingTab={() => {
                    // Switch to counting tab
                    agentChatRef.current?.switchToCountingTab?.();
                  }}
                  onSwitchToWiresTab={() => {
                    // Switch to counting tab with wires subtab
                    console.log('🔌 EstimationDetail: onSwitchToWiresTab called, ref exists:', !!agentChatRef.current, 'method exists:', !!agentChatRef.current?.switchToWiresTab);
                    // Call immediately
                    agentChatRef.current?.switchToWiresTab?.();
                    // Also retry after a short delay in case of timing issues
                    setTimeout(() => {
                      agentChatRef.current?.switchToWiresTab?.();
                    }, 100);
                  }}
                  onAddComponentToKey={async (component) => {
                    // Add the new component to the key
                    if (!estimation?.id) return;
                    
                    try {
                      // Fetch current key
                      const { data: current, error: fetchError } = await supabase
                        .from('estimations')
                        .select('project_key')
                        .eq('id', estimation.id)
                        .single();
                      
                      if (fetchError) throw fetchError;
                      
                      const currentKey = current?.project_key || { components: [], extractedAt: new Date().toISOString() };
                      
                      // Deduplicate name against existing components
                      const existingNames = (currentKey.components || []).map((c: any) => c.name);
                      const uniqueName = getUniqueComponentName(component.name, existingNames);
                      
                      // Add new component with deduplicated name (if name already existed, it gets a suffix like (2))
                      const newComponent = {
                        name: uniqueName,
                        color: component.color,
                        symbol: component.symbol,
                        pages: [],
                        totalCount: 0
                      };
                      
                      const updatedKey = {
                        ...currentKey,
                        components: [...(currentKey.components || []), newComponent]
                      };
                      
                      // Save to database
                      const { error: saveError } = await supabase
                        .from('estimations')
                        .update({ project_key: updatedKey })
                        .eq('id', estimation.id);
                      
                      if (saveError) throw saveError;
                      
                      // Update local state
                      setElectricalKey(updatedKey);
                      
                      // Select the new component for placement
                      setActivePlacementComponent({
                        name: uniqueName,
                        symbol: component.symbol,
                        color: component.color
                      });
                      
                      // Trigger refresh
                      setRefreshTrigger(prev => prev + 1);
                      setCountingModeActive(true);
                    } catch (error) {
                      console.error('Failed to add component to key:', error);
                    }
                  }}
                  agentChatRef={agentChatRef}
                  pageIndex={pageIndex}
                  onReindex={handleReindex}
                />
              </div>

              {/* Right: Agent Chat */}
              <div className={`chat-sidebar ${isAgentChatCollapsed ? 'collapsed' : ''}`}>
                {isExtractingKey && (
                  <div className="extraction-overlay">
                    <div className="extraction-message">
                      <span className="extraction-spinner">⏳</span>
                      <span>Extracting Key...</span>
                      {extractionProgress && (
                        <span className="extraction-progress">{extractionProgress}</span>
                      )}
                    </div>
                  </div>
                )}
                <NewAgentChat
                  ref={agentChatRef}
                  estimation={estimation}
                  specsFiles={specsFiles}
                  drawingPdfUrl={currentBlueprintFile?.url}
                  onNavigateToSpecsPage={handleNavigateToSpecsPage}
                  onNavigateToDrawingPage={(pageNumber) => {
                    console.log('📐 Navigating to drawing page:', pageNumber);
                    // Switch to draw mode and navigate to page
                    setActiveMode('draw');
                    setTimeout(() => {
                      if (pdfAnnotatorRef.current?.navigateToPage) {
                        pdfAnnotatorRef.current.navigateToPage(pageNumber);
                      }
                    }, 300);
                  }}
                  onNavigateToNecaPage={(pageNumber) => {
                    console.log('📖 Navigating to NECA page:', pageNumber);
                    // Switch to NECA mode with delay to prevent rendering cancel
                    setActiveMode('neca');
                    setNecaPageNumber(pageNumber);
                    // Navigate to the page after transition
                    setTimeout(() => {
                      if (pdfAnnotatorRef.current?.navigateToNecaPage) {
                        pdfAnnotatorRef.current.navigateToNecaPage(pageNumber);
                      }
                    }, 500); // Longer delay for mode switch and file load
                  }}
                  onNavigateToEstimate={() => {
                    console.log('💰 Navigating to estimate table...');
                    setActiveMode('estimate');
                  }}
                  onCollapse={() => setIsAgentChatCollapsed(true)}
                  pdfAnnotatorRef={pdfAnnotatorRef}
                  onAnnotationCountsChange={setAnnotationCounts}
                  filteredComponent={filteredComponent}
                  onFilteredComponentChange={setFilteredComponent}
                  onTabChange={(tab) => {
                    setCountingModeActive(tab === 'counting');
                    if (tab !== 'counting') setActivePlacementComponent(null);
                  }}
                  selectedForPlacement={activePlacementComponent ? (activePlacementComponent.symbolType === 'Lighting' ? (activePlacementComponent.symbol || activePlacementComponent.name) : activePlacementComponent.name) : null}
                  onComponentSelectForPlacement={setActivePlacementComponent}
                  refreshTrigger={refreshTrigger}
                  activeMode={activeMode}
                />
              </div>
            </div>
          </div>

          {/* Bottom: Key Panel (only show in draw mode) */}
          {!isKeyPanelCollapsed && activeMode === 'draw' && (
            <ProjectKeyPanelTree
              estimationId={estimation.id || ''}
              onComponentSelect={(component) => {
                console.log('Selected component:', component);
              }}
              onKeyUpdate={(updatedKey) => {
                console.log('🔄 Electrical key updated from UI, updating state...', {
                  componentCount: updatedKey?.components?.length || 0
                });
                
                // Detect count changes and update estimate table
                if (electricalKey && updatedKey && estimation?.id) {
                  updatedKey.components.forEach(async (component: any) => {
                    const oldComponent = electricalKey.components?.find((c: any) => 
                      c.name.toLowerCase() === component.name.toLowerCase()
                    );
                    
                    if (oldComponent && oldComponent.totalCount !== component.totalCount) {
                      console.log(`📊 Count changed for ${component.name}: ${oldComponent.totalCount} → ${component.totalCount}`);
                      
                      // Update estimate table quantity (use matched component if available)
                      const success = await updateEstimateQuantity(
                        estimation.id,
                        component.name,
                        component.totalCount,
                        component.estimate_matched_component // Use saved correlation
                      );
                      
                      if (!success) {
                        console.warn(`⚠️ Could not find estimate row for "${component.name}"`);
                        console.log(`💡 TIP: Run auto-estimate for "${component.name}" to create an estimate entry first`);
                      } else {
                        console.log(`✅ Successfully updated estimate for "${component.name}" to ${component.totalCount}`);
                      }
                    }
                  });
                }
                
                setElectricalKey(updatedKey);
                // Avoid unconditional annotation reloads here.
                // Key updates (color/metadata changes) do not require re-fetching annotation rows and
                // can race with in-flight saves. Explicit mutation flows trigger targeted reloads.
              }}
              pdfAnnotatorRef={pdfAnnotatorRef}
              currentFile={currentFile}
              onCollapse={() => setIsKeyPanelCollapsed(true)}
              onAnnotationCountsChange={(counts) => {
                console.log('🎯 EstimationDetail: onAnnotationCountsChange CALLED');
                console.log('🔍 Annotation counts updated:', {
                  size: counts.size,
                  components: Array.from(counts.keys())
                });
                console.log('🔑 Context check:', {
                  hasEstimation: !!estimation?.id,
                  hasElectricalKey: !!electricalKey,
                  hasComponents: !!electricalKey?.components,
                  componentCount: electricalKey?.components?.length || 0
                });
                
                // Update estimate table when manual placement counts change
                if (estimation?.id && electricalKey?.components) {
                  counts.forEach((pageCounts, componentName) => {
                    // Calculate total count for this component across all pages
                    const newTotalCount = Array.from(pageCounts.values()).reduce((sum, count) => sum + count, 0);
                    const hadPreviousCount = previousAnnotationCountsRef.current.has(componentName);
                    const previousTotalCount = previousAnnotationCountsRef.current.get(componentName) || 0;
                    
                    // Only update if we had a previous count AND it changed (skip initial load)
                    if (hadPreviousCount && previousTotalCount !== newTotalCount) {
                      console.log(`📊 Manual placement count changed for ${componentName}: ${previousTotalCount} → ${newTotalCount}`);
                      
                      // Find the component in key to get matched_component
                      const keyComponent = electricalKey.components.find((c: any) =>
                        c.name.toLowerCase() === componentName.toLowerCase()
                      );
                      
                      // Update estimate table quantity (use saved correlation if available)
                      updateEstimateQuantity(
                        estimation.id,
                        componentName,
                        newTotalCount,
                        keyComponent?.estimate_matched_component
                      ).then(success => {
                        if (!success) {
                          console.warn(`⚠️ MANUAL PLACEMENT: Could not find estimate row for "${componentName}"`);
                          console.log(`💡 TIP: Run auto-estimate for "${componentName}" to create an estimate entry first`);
                        } else {
                          console.log(`✅ Successfully updated estimate for "${componentName}" to ${newTotalCount}`);
                        }
                      });
                    }
                    
                    // Update ref with new count
                    previousAnnotationCountsRef.current.set(componentName, newTotalCount);
                  });
                }
                
                setAnnotationCounts(counts);
              }}
              refreshTrigger={refreshTrigger}
              extractionProgress={keyExtractionProgress}
              onStartExtraction={() => {
                setAutoExtractKeyOnSnapshot(true);
                setKeyExtractionProgress('');
              }}
            />
          )}
        </div>
      </div>
    </BlueprintProvider>
    </AnnotationSaveProvider>
  );
}

export default EstimationDetail;

